import { Link } from "react-router-dom";
import { Check, ArrowRight, Star } from "lucide-react";
import { PageHero, SectionLabel } from "../components/site/Bits";
import Reveal from "../components/site/Reveal";
import { IMAGES, VALET_PACKAGES, VALET_EXTRAS } from "../data/content";

export default function MobileCarValeting() {
  return (
    <div data-testid="valeting-page">
      <PageHero
        crumb="Mobile Car Valeting"
        label="Mobile Car Valeting"
        title="A showroom finish, on your driveway."
        subtitle="Fully mobile valeting packages for every budget — we bring the shine to you, right across Lancashire."
        image={IMAGES.carPolish}
      />

      {/* Packages */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><SectionLabel>Packages</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                Choose your level of shine.
              </h2>
            </Reveal>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {VALET_PACKAGES.map((pkg, i) => (
              <Reveal key={pkg.name} delay={i * 0.08}>
                <div
                  data-testid={`valet-package-${pkg.tier.toLowerCase()}`}
                  className={`relative h-full rounded-3xl p-8 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 ${
                    pkg.popular
                      ? "bg-navy text-white shadow-[0_20px_50px_rgba(31,58,95,0.25)]"
                      : "bg-mist text-navy border border-black/5"
                  }`}
                >
                  {pkg.popular && (
                    <span className="absolute top-6 right-6 inline-flex items-center gap-1 rounded-full bg-brandgreen px-3 py-1 text-[11px] font-semibold text-white">
                      <Star className="w-3 h-3 fill-white" /> Popular
                    </span>
                  )}
                  <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${pkg.popular ? "text-brandgreen" : "text-brandgreen"}`}>
                    {pkg.tier}
                  </span>
                  <h3 className="mt-3 font-heading font-extrabold text-2xl tracking-tight">{pkg.name}</h3>
                  <div className="mt-4 font-heading font-extrabold text-5xl tracking-tighter">
                    {pkg.price}
                  </div>
                  <p className={`mt-3 text-sm ${pkg.popular ? "text-white/70" : "text-muted-foreground"}`}>
                    {pkg.blurb}
                  </p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${pkg.popular ? "text-brandgreen" : "text-brandgreen"}`} strokeWidth={3} />
                        <span className={pkg.popular ? "text-white/85" : "text-navy/80"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/book"
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors duration-300 ${
                      pkg.popular
                        ? "bg-brandgreen text-white hover:bg-white hover:text-navy"
                        : "bg-navy text-white hover:bg-brandgreen"
                    }`}
                  >
                    Book Your Valet
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Extras */}
      <section className="py-20 lg:py-28 bg-mist">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-12 items-center">
            <div className="lg:col-span-5">
              <Reveal><SectionLabel>Optional Extras</SectionLabel></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl tracking-tighter">
                  Add the finishing touches.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 text-muted-foreground leading-relaxed">
                  Tailor any package with our optional extras. Just let us know when you book.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="flex flex-wrap gap-3">
                {VALET_EXTRAS.map((e, i) => (
                  <Reveal key={e} delay={i * 0.05}>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white border border-black/5 px-5 py-3 font-medium text-navy">
                      <span className="w-2 h-2 rounded-full bg-brandgreen" />
                      {e}
                    </span>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="rounded-3xl bg-navy text-white p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter max-w-lg">
              Ready to see your car transformed?
            </h2>
            <Link
              to="/book"
              data-testid="valet-cta-book"
              className="inline-flex items-center gap-2 rounded-full bg-brandgreen px-8 py-4 font-semibold hover:bg-white hover:text-navy transition-colors duration-300 shrink-0"
            >
              Book Your Valet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
